import NoDataMessage from '../feedback/NoDataMessage.component';
import RaptorLoading from '../feedback/RaptorLoading';

interface QueryBoundaryProps<T> {
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  error?: any;
  data: T | undefined | null;

  children: (data: T) => React.ReactNode;

  // Optional overrides
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  noDataComponent?: React.ReactNode;

  // Fallback messages (used only if override components are not provided)
  loadingMessages?: string[];
  errorMessage?: string;
  noDataMessage?: string;

  // When true, default components use compact mode (auto height) instead of full screen
  card?: boolean;
}

export function QueryBoundary<T>({
  isLoading,
  isFetching,
  isError,
  error,
  data,
  children,
  loadingComponent,
  errorComponent,
  noDataComponent,
  loadingMessages = ['Loading...'],
  errorMessage = 'An error occurred while loading data.',
  noDataMessage = 'No data available.',
  card = false,
}: QueryBoundaryProps<T>) {
  // When card mode, use auto height; otherwise use full screen
  const height = card ? 'auto' : undefined;

  // Default loading handling - can pass custom loading component
  if (isLoading) {
    return (
      <>
        {loadingComponent ?? (
          <RaptorLoading centerWrap messages={loadingMessages} setHeight={height} />
        )}
      </>
    );
  }

  //   Default error handling - can pass custom error component
  if (isError) {

    const resolvedErrorMessage =
      (error as any)?.response?.status === 403 ||
        error?.message?.includes('status code 403')
        ? 'You are not authorised to access this resource. Please contact RiskSystem support to request access.'
        : errorMessage;

    return (
      <>
        {errorComponent ?? (
          <NoDataMessage message={resolvedErrorMessage} setHeight={height} />
        )}
      </>
    );
  }

  //   Default no data handling - can pass custom no data component
  if (!data || (Array.isArray(data) && data.length === 0)) {
    return (
      <>
        {noDataComponent ?? (
          <NoDataMessage message={noDataMessage} setHeight={height} />
        )}
        {isFetching && (
          <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-white/70">
            <RaptorLoading />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {children(data)}
      {isFetching && (
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-white/70">
          <RaptorLoading />
        </div>
      )}
    </>
  );
}
